import { useCallback } from "react";
import localforage from "localforage";
import { notification, message } from "antd";

export function useAuthLogic(API_URL, state, externalMethods) {
  const {
    setIsLoggedIn,
    setShowAuthModal,
    setShowSidebar,
    setQueueStatus,
    setCurrentNumber,
    setBusinessName,
    setUserId,
    setUserAddress,
    setCurrentLocation,
    setBusinessMessage,
    profileForm,
    setShowUserStatus,
    setLoading,
    setButtonLoading,
    currentLocation,
    tempLocation,
    setShowUpdateProfileModal,
    customBusinessType,
    setShowVerificationModal,
    setOtpSent,
    setOtpResendTimer,
    otpTimerCount,
    setOtpTimerCount,
    otp,
    setOtpVerified,
    setShowResetPasswordModal,
    form2,
  } = state;

  const { fetchNearbyBusinesses, requestLocation, fetchBusinessTypes } = externalMethods;

  const updateProfile = useCallback(
    async (values) => {
      setButtonLoading(true);
      const token = await localforage.getItem("access_token");
      const { currentAddress, ...rest } = values;
      if (tempLocation) {
        setCurrentLocation(tempLocation);
        await localforage.setItem("LocationDetails", tempLocation);
      }
      try {
        const response = await fetch(`${API_URL}/updateProfile`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...rest,
            location: tempLocation || currentLocation,
          }),
        });
        const data = await response.json();
        if (data.error) {
          notification.error({ message: data.error });
        } else {
          notification.success({ message: "Profile updated successfully" });
          setShowUpdateProfileModal(false);
          setBusinessName(values.businessName);
        }
      } catch (error) {
        notification.error({ message: "Error updating profile" });
        console.error("Error updating profile:", error);
      } finally {
        setButtonLoading(false);
      }
    },
    [API_URL, currentLocation, tempLocation, setButtonLoading, setCurrentLocation, setShowUpdateProfileModal, setBusinessName]
  );

  const signup = useCallback(
    async (userData) => {
      const businessType =
        userData.businessType === "other"
          ? customBusinessType
          : userData.businessType;
      const location = await localforage.getItem("LocationDetails");
      if (location) {
        setLoading(true);
        setButtonLoading(true);
        try {
          const response = await fetch(`${API_URL}/signup`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...userData,
              businessType,
              location,
            }),
          });
          const data = await response.json();
          if (data.error) {
            notification.error({ message: data.error });
          } else {
            await localforage.setItem("access_token", data.token);
            setIsLoggedIn(true);
            fetchNearbyBusinesses(location);
            setShowUserStatus(true);
            setShowAuthModal(false);
            setShowVerificationModal(true);
            setBusinessName(userData.businessName);
            setUserAddress(currentLocation?.address);
          }
        } catch (error) {
          notification.error({ message: "Signup failed" });
          console.error("Signup failed:", error);
        } finally {
          setLoading(false);
          setButtonLoading(false);
        }
      } else {
        notification.warning({ message: "Please provide location access" });
      }
    },
    [
      API_URL,
      customBusinessType,
      currentLocation?.address,
      fetchNearbyBusinesses,
      setLoading,
      setButtonLoading,
      setIsLoggedIn,
      setShowUserStatus,
      setShowAuthModal,
      setShowVerificationModal,
      setBusinessName,
      setUserAddress
    ]
  );

  const handleLoginSuccess = useCallback(
    (data) => {
      setShowAuthModal(false);
      setShowSidebar(false);
      setIsLoggedIn(true);
      setQueueStatus(data.isActive);
      setCurrentNumber(data.queueNumber);
      setBusinessName(data.businessName);
      setUserId(data.userId);
      setUserAddress(data.address);
      setCurrentLocation(data.locationDetails);
      setBusinessMessage(data.businessMessage);
      profileForm.setFieldsValue({
        businessName: data.businessName,
        mobileNumber: data.mobileNumber,
        email: data.email,
        currentAddress: data.location?.address,
      });
      setShowUserStatus(!data.isVerified);
    },
    [profileForm, setShowAuthModal, setShowSidebar, setIsLoggedIn, setQueueStatus, setCurrentNumber, setBusinessName, setUserId, setUserAddress, setCurrentLocation, setBusinessMessage, setShowUserStatus]
  );

  const login = useCallback(
    async ({ emailOrMobile, password }) => {
      setLoading(true);
      setButtonLoading(true);
      try {
        const response = await fetch(`${API_URL}/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: emailOrMobile,
            mobileNumber: emailOrMobile,
            password,
          }),
        });
        const data = await response.json();
        if (data.error) {
          notification.error({ message: data.error });
        } else {
          await localforage.setItem("access_token", data.token);
          handleLoginSuccess(data);
        }
      } catch (error) {
        notification.error({ message: "Login failed" });
        console.error("Login failed:", error);
      } finally {
        setLoading(false);
        setButtonLoading(false);
      }
    },
    [API_URL, handleLoginSuccess, setLoading, setButtonLoading]
  );

  const logOut = useCallback(async () => {
    setLoading(true);
    const token = await localforage.getItem("access_token");
    try {
      await fetch(`${API_URL}/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      await localforage.removeItem("access_token");
      setIsLoggedIn(false);
      setShowSidebar(false);
      setShowUserStatus(false);
      setCurrentNumber(0);
      fetchBusinessTypes();
      const location = await localforage.getItem("LocationDetails");
      if (location) {
        setCurrentLocation(location);
      } else {
        requestLocation(true);
      }
    } catch (error) {
      notification.error({ message: "Logout failed" });
      console.error("Logout failed:", error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, fetchBusinessTypes, requestLocation, setLoading, setIsLoggedIn, setShowSidebar, setShowUserStatus, setCurrentNumber, setCurrentLocation]);

  const startOtpResendTimer = useCallback(() => {
    setOtpResendTimer(true);
    let timer = otpTimerCount;
    const interval = setInterval(() => {
      timer -= 1;
      setOtpTimerCount(timer);
      if (timer <= 0) {
        clearInterval(interval);
        setOtpResendTimer(false);
        setOtpTimerCount(30);
      }
    }, 1000);
  }, [otpTimerCount, setOtpResendTimer, setOtpTimerCount]);

  const handleSendOtp = useCallback(
    async (mobileNumber, resend) => {
      setButtonLoading(true);
      try {
        const response = await fetch(`${API_URL}/forgotPassword`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mobileNumber }),
        });
        const data = await response.json();
        if (data.error) {
          notification.error({ message: data.error });
        } else {
          if (resend) {
            message.success("OTP Resent!");
          } else {
            message.success("OTP Sent!");
            setOtpSent(true);
          }
          startOtpResendTimer();
        }
      } catch (error) {
        notification.error({ message: "Error sending OTP" });
        console.error("Error sending OTP:", error);
      } finally {
        setButtonLoading(false);
      }
    },
    [API_URL, startOtpResendTimer, setButtonLoading, setOtpSent]
  );

  const handleVerifyOtp = useCallback(async () => {
    setButtonLoading(true);
    try {
      const response = await fetch(`${API_URL}/verifyOtp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobileNumber: form2.getFieldInstance("mobileNumber")?.input?.value,
          otp,
        }),
      });
      const data = await response.json();
      if (data.error) {
        notification.error({ message: data.error });
      } else {
        notification.success({ message: "OTP verified successfully" });
        setShowAuthModal(false);
        setOtpVerified(true);
        setShowResetPasswordModal(true);
      }
    } catch (error) {
      notification.error({ message: "Error verifying OTP" });
      console.error("Error verifying OTP:", error);
    } finally {
      setButtonLoading(false);
    }
  }, [API_URL, form2, otp, setButtonLoading, setShowAuthModal, setOtpVerified, setShowResetPasswordModal]);

  const handleResetPassword = useCallback(
    async ({ newPassword }) => {
      setButtonLoading(true);
      try {
        const response = await fetch(`${API_URL}/updatePassword`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            mobileNumber: form2.getFieldInstance("mobileNumber")?.input?.value,
            otp,
            newPassword,
          }),
        });
        const data = await response.json();
        if (data.error) {
          notification.error({ message: data.error });
        } else {
          await localforage.setItem("access_token", data.token);
          notification.success({ message: "Password updated successfully" });
          handleLoginSuccess(data);
          setShowResetPasswordModal(false);
        }
      } catch (error) {
        notification.error({ message: "Error updating password" });
        console.error("Error updating password:", error);
      } finally {
        setButtonLoading(false);
      }
    },
    [API_URL, form2, otp, handleLoginSuccess, setButtonLoading, setShowResetPasswordModal]
  );

  return {
    updateProfile, signup, login, logOut, handleSendOtp, handleVerifyOtp, handleResetPassword, handleLoginSuccess
  };
}
